[file name]: CoreValues.vue
[file content begin]
<template>
    <section class="core-values">
        <!-- 内容容器前置 -->
        <div class="content-wrapper">
            <!-- 章节标题 -->
            <div class="section-header">
                <h2 class="section-title">
                    <span class="title-deco"></span>
                    Connecting the World
                    <div class="title-line-good"></div>
                </h2>
                <p class="section-subtitle">Quality-Driven Cross-Border Commerce Paradigm</p>
            </div>

            <!-- 主旨卡片容器 -->
            <div class="values-grid">
                <article
                    v-for="(value, index) in values"
                    :key="index"
                    class="value-card"
                >
                    <div class="card-ornament"></div>
                    <div class="card-content">
                        <div class="icon-wrapper">
                            <component :is="value.icon" class="feature-icon"></component>
                        </div>
                        <h3 class="value-title">{{ value.title }}</h3>
                        <p class="value-desc">{{ value.description }}</p>
                    </div>
                    <div class="hover-light"></div>
                </article>
            </div>
        </div>

        <!-- 3D视差背景后置 -->
        <div class="parallax-background">
            <div class="depth-layer" data-depth="0.4"></div>
        </div>
    </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { GlobeAltIcon, UsersIcon, CpuChipIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline'

gsap.registerPlugin(ScrollTrigger)

export default {
    data() {
        return {
            values: [
                {
                    icon: GlobeAltIcon,
                    title: "Global Network",
                    description: "Service coverage across major global trade markets."
                },
                {
                    icon: UsersIcon,
                    title: "Client First",
                    description: "Prioritizing client needs at every stage of service delivery."
                },
                {
                    icon: CpuChipIcon,
                    title: "Smart Automation",
                    description: "SaaS-based intelligent scheduling boosts operational efficiency."
                },
                {
                    icon: ShieldCheckIcon,
                    title: "Quality Assurance",
                    description: "Rigorous quality inspections ensure consistent product performance."
                }
            ]
        }
    },
    mounted() {
        this.$nextTick(() => {
            // this.initParallax()
            this.initAnimations()
            // 强制ScrollTrigger重新计算
            // setTimeout(() => ScrollTrigger.refresh(), 500)
        })
    },
    methods: {
        initAnimations() {
            ScrollTrigger.getAll().forEach(t => t.kill());

            // 章节标题动画简化
            gsap.from('.section-title', {
                y: 40,
                opacity: 0,
                duration: 1.2,
                ease: 'power4.out',
                scrollTrigger: {
                    trigger: '.content-wrapper',
                    start: 'top 85%',  // 提高触发位置
                    toggleActions: 'play none none none',
                    invalidateOnRefresh: true,
                }
            });

            // 卡片批量动画（减少ScrollTrigger实例）
            const cards = gsap.utils.toArray('.value-card');
            gsap.from(cards, {
                y: 60,  // 减少运动距离
                opacity: 0,
                duration: 0.8,
                stagger: 0.15,  // 使用stagger替代单独delay
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.values-grid',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true  // 只播放一次
                }
            });
        },
        initParallax() {
            gsap.to('.depth-layer', {
                scrollTrigger: {
                    scrub: 1,
                    invalidateOnRefresh: true
                },
                y: () => -ScrollTrigger.maxScroll(window) * 0.2,
                ease: 'none'
            })
        },
    },
    beforeUnmount() {
        ScrollTrigger.killAll(); // 卸载时清理所有动画
    }
}
</script>

<style lang="scss" scoped>
.core-values {
    position: relative;
    padding: 10rem 0 15rem;
    background: linear-gradient(45deg, #0A1A2F 30%, #12243D 100%);
    isolation: isolate;
    overflow: visible; /* 恢复overflow防止边缘溢出 */
    background: transparent; /* 原背景色 #0A1A2F 移除 */
    backdrop-filter: blur(20px);
    border: 1px solid rgba(197, 164, 126, 0.1);
    transform: translateZ(0); // 启用硬件加速
    will-change: transform; // 正确声明变化属性

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
                to bottom,
                rgba(14, 32, 55, 0.7) 0%,
                rgba(10, 26, 47, 0.9) 5%
        );
    }

}

.card-ornament {
    pointer-events: none; /* 防止装饰元素拦截事件 */
}

.parallax-background {
    position: absolute;
    width: 100vw;
    height: 120%;
    top: -10%;
    left: 0;
    z-index: 0;
    pointer-events: none; /* 允许点击穿透 */

    //.depth-layer {
    //  width: 100%;
    //  height: 100%;
    //  background: radial-gradient(circle at 70% 30%, rgba(197, 164, 126, 0.05) 0%, transparent 30%),
    //  linear-gradient(45deg, transparent 60%, rgba(16, 36, 61, 0.3));
    //  backdrop-filter: blur(20px);
    //}
}


.content-wrapper {
    position: relative;
    z-index: 2;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 2rem;
}

.section-header {
    text-align: center;
    margin-bottom: 6rem;
}

.section-title {
    font-size: 2.8rem;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.95);
    position: relative;
    display: inline-block;
    margin-bottom: 1.5rem;

    .title-deco {
        color: #C5A47E;
        margin-right: 1rem;
        opacity: 0.6;
    }

    .title-underline {
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 60%;
        height: 2px;
        background: linear-gradient(90deg, #C5A47E, transparent);
    }
}

.section-subtitle {
    font-size: 1.1rem;
    letter-spacing: 0.2em;
    color: rgba(255, 255, 255, 0.7);
}

.values-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
    position: relative;
    z-index: 2;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 2%;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 3rem;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.value-card {
    will-change: translate, scale;
    transform: translateZ(0) translateY(0) scale(1) rotateZ(0deg);
    backface-visibility: hidden;
    perspective: 1000px;
    position: relative;
    background:
    linear-gradient(145deg,
      rgba(16,36,61,0.9) 0%,
      rgba(10,26,47,0.95) 100%
    ),
    repeating-linear-gradient(
      0deg,
      rgba(0,0,0,0.1) 0px,
      rgba(0,0,0,0.1) 1px,
      transparent 1px,
      transparent 3px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(0,0,0,0.1) 0px,
      rgba(0,0,0,0.1) 1px,
      transparent 1px,
      transparent 3px
    );
    border: 2px solid rgba(197, 164, 126, 0.1);
    border-radius: 16px;
    padding: 2.5rem;
    overflow: hidden;
    z-index: 3;
    translate: 0 0;
    scale: 1;
    rotate: 0deg;
    transition: translate 0.4s ease,
    scale 0.4s ease,
    rotate 0.4s ease,
    box-shadow 0.4s;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(120deg, transparent 40%, rgba(197, 164, 126, 0.03) 100%);
    }

    &:hover {
    transform: translateY(-8px);
    box-shadow: 0 1px 10px rgba(197, 164, 126, 0.3);
    }
}

.card-content {
    position: relative;
    z-index: 2;
}

.icon-wrapper {
    width: 64px;
    height: 64px;
    background: rgba(197, 164, 126, 0.1);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;

    .feature-icon {
        width: 32px;
        height: 32px;
        color: #C5A47E;
        stroke-width: 1.5;
    }
}

.value-title {
    font-size: 1.4rem;
    font-weight: 500;
    color: #fff;
    margin-bottom: 1rem;
}

.value-desc {
    font-size: 0.95rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0;
}

.value-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(120deg, transparent 40%, rgba(197, 164, 126, 0.03) 100%);
    pointer-events: none; /* 防止伪元素拦截鼠标事件 */
}

.hover-light {
    position: absolute;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(197, 164, 126, 0.08) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.4s ease-out, transform 0.4s ease-out;
    z-index: 1;
    top: 50%;
    left: 50%;
    display: none; // 移除复杂的光效渐变
}

@media (max-width: 768px) {
    .section-title {
        font-size: 2rem;
    }

    .values-grid {
        grid-template-columns: 1fr;
        padding: 0;
    }

    .value-card {
        transition-duration: 0.3s; // 缩短动画时间
        padding: 1.5rem;
    }
}
</style>
[file content end]
